/** composable for using @/components/date-range-picker/date-range-picker.vue along with saving result in URL */

import {
  useUrlSyncedRef,
} from '@qmulus/ui/composable/use-state-url.ts';
import {
  isDefined,
} from '@qmulus/ui/utils/common.ts';
import isEqual from 'lodash.isequal';
import {
  ref, watch,
} from 'vue';

import {
  DEFAULT_RANGES_LIST,
  ECustomRangeType,
  IDateInterval,
  IRangeByJob,
  LAST_JOB_ID,
  MAX_ABS_DATE,
  MIN_ABS_DATE,
  TChoice,
  TRange, dateRangePickerRangeParam,
  rangeIdToRange,
  rangeToDateInterval,
} from '@/components/date-range-picker/date-range-picker.ts';
import {
  findJobs,
} from '@/generated/qmulus/axios/core';
import {
  useJobStore,
} from '@/store/pinia/job';

export const RANGE_QUERY_KEY = 'range';
export const LAST_JOB_SORT_BY = 'finishedOrPlannedTimeAtUtc.desc:nullsFirst,startedOrPlannedTimeAtUtc.desc';

export async function enrichByJobRange(range: IRangeByJob, assetId: number): Promise<IRangeByJob> {
  const jobStore = useJobStore();

  let jobDetails;
  if (range.jobId === LAST_JOB_ID) {
    const response = await findJobs({
      query: {
        assetIds: [
          assetId,
        ],
        sortBy: LAST_JOB_SORT_BY,
        page: 1,
        pageSize: 1,
        start: MIN_ABS_DATE.toISOString(),
        end: MAX_ABS_DATE.toISOString(),
      },
    });
    jobDetails = response.data.items[0];
  } else {
    jobDetails = await jobStore.getCachedJob(range.jobId);
  }

  if (!jobDetails) {
    return range;
  }

  return {
    ...range,
    startedOrPlannedTimeAtUtc: jobDetails.startedOrPlannedTimeAtUtc,
    finishedOrPlannedTimeAtUtc: jobDetails.finishedOrPlannedTimeAtUtc,
    previousJobId: jobDetails.previousJobId,
    nextJobId: jobDetails.nextJobId,
    jobName: jobDetails.displayName,
  };
}

export function useQueryDateRangePicker({
  defaultRange,
  assetId,
}: {
  defaultRange?: TChoice,
  assetId?: number,
} = {}) {
  // Ref is required, as only certain attributes of the object may change
  const activeRange = ref<TRange>(
    (
      typeof defaultRange === 'string'
        ? rangeIdToRange(defaultRange)
        : defaultRange
    )
    || rangeIdToRange(DEFAULT_RANGES_LIST[0]),
  );
  // We can not use computed here, as a relative range would never change, but the actual interval needs to change.
  // Ref is required, as only certain attributes of the object may change
  const activeInterval = ref<IDateInterval>(rangeToDateInterval(activeRange.value));

  const {
    state,
  } = useUrlSyncedRef({
    [RANGE_QUERY_KEY]: dateRangePickerRangeParam(activeRange.value),
  });

  let resolveToken = 0;

  watch(
    () => state.value[RANGE_QUERY_KEY],
    async (newValue, oldValue) => {
      if (isEqual(newValue, oldValue)) {
        return;
      }

      const currentToken = ++resolveToken;
      const resolvedRange = newValue.rangeType === ECustomRangeType.BY_JOB && isDefined(assetId)
        ? await enrichByJobRange(newValue, assetId)
        : newValue;

      if (currentToken !== resolveToken) {
        return;
      }

      activeRange.value = {
        ...resolvedRange,
      };
      activeInterval.value = rangeToDateInterval(resolvedRange);
    },
    {
      immediate: true,
    },
  );
  watch(
    () => activeRange.value,
    (newValue, oldValue) => {
      if (isEqual(newValue, oldValue)) {
        return;
      }
      state.value[RANGE_QUERY_KEY] = {
        ...newValue,
      };
    },
  );

  return {
    activeRange,
    activeInterval,
  };
}
